import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/page-header';
import { Info, CheckCircle2, AlertCircle } from 'lucide-react';

// Sample data with many columns to demonstrate scrolling
const SAMPLE_DATA = [
  {
    id: '1',
    projectName: 'Digital Transformation Initiative',
    client: 'Acme Corporation',
    manager: 'Sarah Johnson',
    budget: '£250,000',
    spent: '£180,000',
    revenue: '£220,000',
    margin: '18%',
    status: 'In Progress',
    health: 'Green',
    team: 8,
    hours: 2400,
    utilisation: '85%',
    startDate: '01/03/2024',
    endDate: '30/09/2024',
    completion: '65%',
    risk: 'Low',
    priority: 'High',
    department: 'Technology',
    location: 'London',
    lastUpdate: '2 hours ago',
  },
  {
    id: '2',
    projectName: 'Cloud Migration Project',
    client: 'TechStart Ltd',
    manager: 'Michael Chen',
    budget: '£180,000',
    spent: '£195,000',
    revenue: '£180,000',
    margin: '-8%',
    status: 'At Risk',
    health: 'Red',
    team: 6,
    hours: 1800,
    utilisation: '92%',
    startDate: '15/01/2024',
    endDate: '15/06/2024',
    completion: '78%',
    risk: 'High',
    priority: 'Critical',
    department: 'Infrastructure',
    location: 'Manchester',
    lastUpdate: '1 day ago',
  },
  {
    id: '3',
    projectName: 'ERP Implementation',
    client: 'Global Industries',
    manager: 'Rachel Green',
    budget: '£500,000',
    spent: '£420,000',
    revenue: '£480,000',
    margin: '12%',
    status: 'In Progress',
    health: 'Amber',
    team: 12,
    hours: 4800,
    utilisation: '88%',
    startDate: '01/02/2024',
    endDate: '31/12/2024',
    completion: '45%',
    risk: 'Medium',
    priority: 'High',
    department: 'Business Systems',
    location: 'Birmingham',
    lastUpdate: '3 hours ago',
  },
  {
    id: '4',
    projectName: 'Mobile App Development',
    client: 'Retail Solutions Inc',
    manager: 'James Wilson',
    budget: '£120,000',
    spent: '£85,000',
    revenue: '£110,000',
    margin: '21%',
    status: 'In Progress',
    health: 'Green',
    team: 5,
    hours: 1200,
    utilisation: '78%',
    startDate: '01/04/2024',
    endDate: '30/08/2024',
    completion: '55%',
    risk: 'Low',
    priority: 'Medium',
    department: 'Digital',
    location: 'Leeds',
    lastUpdate: '5 hours ago',
  },
  {
    id: '5',
    projectName: 'Data Analytics Platform',
    client: 'Finance Corp',
    manager: 'Emma Thompson',
    budget: '£350,000',
    spent: '£280,000',
    revenue: '£340,000',
    margin: '17%',
    status: 'In Progress',
    health: 'Green',
    team: 10,
    hours: 3200,
    utilisation: '91%',
    startDate: '15/02/2024',
    endDate: '15/10/2024',
    completion: '62%',
    risk: 'Low',
    priority: 'High',
    department: 'Analytics',
    location: 'Edinburgh',
    lastUpdate: '30 minutes ago',
  },
];

export function ResponsiveTableDemo() {
  const getHealthBadge = (health: string) => {
    const variants = {
      Green: 'default' as const,
      Amber: 'secondary' as const,
      Red: 'destructive' as const,
    };
    return <Badge variant={variants[health as keyof typeof variants]}>{health}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    if (status === 'At Risk') {
      return <Badge variant="destructive">{status}</Badge>;
    }
    return <Badge variant="default">{status}</Badge>;
  };

  const getRiskBadge = (risk: string) => {
    const variants = {
      Low: 'default' as const,
      Medium: 'secondary' as const,
      High: 'destructive' as const,
    };
    return <Badge variant={variants[risk as keyof typeof variants]}>{risk}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const variants = {
      Low: 'outline' as const,
      Medium: 'secondary' as const,
      High: 'default' as const,
      Critical: 'destructive' as const,
    };
    return <Badge variant={variants[priority as keyof typeof variants]}>{priority}</Badge>;
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title="Responsive Table Demo"
        description="Demonstration of horizontal scrolling with visual indicators"
      />

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary" />
            How to Use
          </CardTitle>
          <CardDescription>
            This page demonstrates the responsive table implementation with horizontal scrolling
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success-600" />
                Features Demonstrated
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Horizontal scrolling when content exceeds viewport width</li>
                <li>Visible, styled scrollbar at the bottom of the table</li>
                <li>Animated chevron indicators showing scrollable content</li>
                <li>Gradient fade effects on left/right edges</li>
                <li>Responsive behaviour across all screen sizes</li>
                <li>Touch-friendly scrolling on mobile devices</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-info-600" />
                Try These Actions
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Resize your browser window to see responsive behaviour</li>
                <li>Scroll horizontally using the scrollbar</li>
                <li>Watch the chevron indicators appear/disappear</li>
                <li>Notice the gradient fade effects on edges</li>
                <li>Try on mobile/tablet devices for touch scrolling</li>
                <li>Use keyboard arrow keys when table is focused</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wide Table with Many Columns */}
      <Card>
        <CardHeader>
          <CardTitle>Project Portfolio (20 Columns)</CardTitle>
          <CardDescription>
            Scroll horizontally to view all columns. Notice the visual indicators and smooth scrolling.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Project Name</TableHead>
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">Manager</TableHead>
                <TableHead className="font-semibold text-right">Budget</TableHead>
                <TableHead className="font-semibold text-right">Spent</TableHead>
                <TableHead className="font-semibold text-right">Revenue</TableHead>
                <TableHead className="font-semibold text-right">Margin</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Health</TableHead>
                <TableHead className="font-semibold text-right">Team Size</TableHead>
                <TableHead className="font-semibold text-right">Hours</TableHead>
                <TableHead className="font-semibold text-right">Utilisation</TableHead>
                <TableHead className="font-semibold">Start Date</TableHead>
                <TableHead className="font-semibold">End Date</TableHead>
                <TableHead className="font-semibold text-right">Completion</TableHead>
                <TableHead className="font-semibold">Risk</TableHead>
                <TableHead className="font-semibold">Priority</TableHead>
                <TableHead className="font-semibold">Department</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Last Update</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {SAMPLE_DATA.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-medium">{project.projectName}</TableCell>
                  <TableCell>{project.client}</TableCell>
                  <TableCell>{project.manager}</TableCell>
                  <TableCell className="text-right">{project.budget}</TableCell>
                  <TableCell className="text-right">{project.spent}</TableCell>
                  <TableCell className="text-right">{project.revenue}</TableCell>
                  <TableCell className="text-right">
                    <span className={project.margin.startsWith('-') ? 'text-destructive font-medium' : 'text-success-600 font-medium'}>
                      {project.margin}
                    </span>
                  </TableCell>
                  <TableCell>{getStatusBadge(project.status)}</TableCell>
                  <TableCell>{getHealthBadge(project.health)}</TableCell>
                  <TableCell className="text-right">{project.team}</TableCell>
                  <TableCell className="text-right">{project.hours}h</TableCell>
                  <TableCell className="text-right">{project.utilisation}</TableCell>
                  <TableCell>{project.startDate}</TableCell>
                  <TableCell>{project.endDate}</TableCell>
                  <TableCell className="text-right">{project.completion}</TableCell>
                  <TableCell>{getRiskBadge(project.risk)}</TableCell>
                  <TableCell>{getPriorityBadge(project.priority)}</TableCell>
                  <TableCell>{project.department}</TableCell>
                  <TableCell>{project.location}</TableCell>
                  <TableCell className="text-muted-foreground">{project.lastUpdate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Technical Details */}
      <Card>
        <CardHeader>
          <CardTitle>Technical Implementation</CardTitle>
          <CardDescription>
            Details about the responsive table solution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Components Used</h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li><code className="text-xs bg-muted px-1 py-0.5 rounded">Table</code> - Base table component with built-in scroll container</li>
                <li><code className="text-xs bg-muted px-1 py-0.5 rounded">ScrollIndicator</code> - Visual indicators for scrollable content</li>
                <li><code className="text-xs bg-muted px-1 py-0.5 rounded">.scrollbar-visible</code> - Custom scrollbar styling utility class</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Browser Support</h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Chrome/Edge: Full support with custom scrollbar styling</li>
                <li>Firefox: Full support with thin scrollbar</li>
                <li>Safari: Full support with custom scrollbar styling</li>
                <li>Mobile browsers: Touch-friendly scrolling enabled</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Accessibility Features</h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Keyboard navigation support (Tab, Arrow keys)</li>
                <li>Screen reader compatible table structure</li>
                <li>Visual indicators for users who can't see scrollbar</li>
                <li>Proper semantic HTML table markup</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Performance Optimizations</h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Efficient scroll event handling with refs</li>
                <li>ResizeObserver for responsive updates</li>
                <li>CSS-based animations for smooth transitions</li>
                <li>Hardware-accelerated scrolling</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
